/**
 * Neutral transport contract plus the deterministic fake transport used by tests.
 *
 * `Transport` is the only media-plane boundary the client knows. A browser implementation lives in
 * `SfuTransport` at the bottom of this file and is loaded lazily; nothing above it imports the
 * provider package, and no provider type appears in the exported SDK declarations.
 */
import { Emitter } from './emitter.js';
import { normalizeError, rtcError } from './errors.js';
import { emptyStats, toRtcStats } from './stats.js';
/**
 * Deterministic in-memory transport. It performs no device or network I/O, exposes simulation hooks
 * for every remote event, and records every outbound operation for assertions.
 */
export class FakeTransport {
    kind = 'fake';
    emitter;
    participants = new Map();
    subscriptions = new Map();
    streamTypes = new Map();
    dataMessages = [];
    statsByUid = new Map();
    remoteMedia = new Map();
    clock;
    state = 'disconnected';
    joined = false;
    localUid = 0;
    role = 'host';
    audioPublished = false;
    videoPublished = false;
    audioMuted = false;
    videoMuted = false;
    screenShared = false;
    previewing = false;
    encoder = null;
    dualStream = false;
    dynacast = false;
    visibleOnly = false;
    previewToken = null;
    connectAttempts = 0;
    disconnectCount = 0;
    connectFailure = null;
    onceFailure = null;
    reconnectFailureBudget = 0;
    publishDenied;
    constructor(options = {}) {
        this.emitter = new Emitter((error, event) => {
            options.logger?.warn?.('rtc-web: transport event listener failed', String(event), error);
        });
        this.clock = options.clock;
        this.connectFailure = options.failConnect ?? null;
        this.onceFailure = options.failNextConnect ?? null;
        this.publishDenied = options.publishDenied ?? false;
        this.statsByUid.set(0, toRtcStats(options.stats ?? {}));
    }
    on(event, listener) {
        return this.emitter.on(event, listener);
    }
    async connect(options) {
        this.connectAttempts += 1;
        if (this.reconnectFailureBudget > 0) {
            this.reconnectFailureBudget -= 1;
            const failure = rtcError('RTC_RECONNECTING', 'simulated reconnect failure');
            this.state = 'failed';
            this.emitter.emit('connection-state', { state: 'failed', reason: failureText(failure) });
            throw normalizeError(failure);
        }
        if (this.onceFailure !== null) {
            const failure = this.onceFailure;
            this.onceFailure = null;
            this.state = 'failed';
            this.emitter.emit('connection-state', { state: 'failed', reason: failureText(failure) });
            throw normalizeError(failure);
        }
        if (this.connectFailure !== null) {
            this.state = 'failed';
            this.emitter.emit('connection-state', {
                state: 'failed',
                reason: failureText(this.connectFailure),
            });
            throw normalizeError(this.connectFailure);
        }
        this.state = 'connecting';
        this.emitter.emit('connection-state', { state: 'connecting', reason: null });
        this.localUid = options.uid;
        this.role = options.role;
        this.joined = true;
        this.state = 'connected';
        const templateStats = this.statsByUid.get(0);
        if (templateStats) {
            this.statsByUid.delete(0);
            this.statsByUid.set(options.uid, templateStats);
        }
        this.previewToken = options.token;
        this.audioPublished = options.publishAudio;
        this.videoPublished = options.publishVideo;
        this.audioMuted = false;
        this.videoMuted = false;
        this.dualStream = options.dualStream;
        this.dynacast = options.dynacast;
        this.visibleOnly = options.subscribeVisibleOnly;
        this.emitter.emit('connection-state', { state: 'connected', reason: null });
        return {
            uid: options.uid,
            participants: [...this.participants.values()].map(toParticipantSnapshot),
        };
    }
    async disconnect() {
        if (!this.joined) {
            this.state = 'disconnected';
            return;
        }
        this.disconnectCount += 1;
        this.joined = false;
        this.audioPublished = false;
        this.videoPublished = false;
        this.screenShared = false;
        this.subscriptions.clear();
        this.state = 'disconnected';
        this.emitter.emit('connection-state', { state: 'disconnected', reason: 'local-leave' });
    }
    isConnected() {
        return this.joined && this.state === 'connected';
    }
    async publish(mediaType) {
        this.requireConnected();
        if (this.role === 'audience') {
            throw rtcError('RTC_PUBLISH_DENIED', 'audience role cannot publish media');
        }
        if (this.publishDenied)
            throw rtcError('RTC_PUBLISH_DENIED', 'publishing is denied by the token');
        if (mediaType === 'audio')
            this.audioPublished = true;
        else
            this.videoPublished = true;
    }
    async unpublish(mediaType) {
        this.requireConnected();
        if (mediaType === 'audio') {
            this.audioPublished = false;
            this.audioMuted = false;
        }
        else {
            this.videoPublished = false;
            this.videoMuted = false;
        }
    }
    async setMuted(mediaType, muted) {
        this.requireConnected();
        const published = mediaType === 'audio' ? this.audioPublished : this.videoPublished;
        if (!published) {
            throw rtcError('invalid_parameter', `${mediaType} is not published; call publish first`);
        }
        if (mediaType === 'audio')
            this.audioMuted = muted;
        else
            this.videoMuted = muted;
    }
    async setRole(role) {
        this.role = role;
    }
    async publishTrack(_track, source) {
        this.requireConnected();
        if (this.role === 'audience' || this.publishDenied) {
            throw rtcError('RTC_PUBLISH_DENIED', 'publishing is denied for this participant');
        }
        if (source === 'camera')
            this.videoPublished = true;
        else if (source === 'microphone')
            this.audioPublished = true;
        else if (source === 'screen')
            this.screenShared = true;
        else
            this.audioPublished = true;
    }
    async unpublishTrack(source) {
        this.requireConnected();
        if (source === 'camera')
            this.videoPublished = false;
        else if (source === 'microphone')
            this.audioPublished = false;
        else if (source === 'screen')
            this.screenShared = false;
        else
            this.audioPublished = false;
    }
    isMuted(mediaType) {
        return mediaType === 'audio' ? this.audioMuted : this.videoMuted;
    }
    /** The fake transport has no media pipeline. */
    getLocalStream() {
        return null;
    }
    /** Test hook: attach a synthetic media track so remote-track APIs can be exercised offline. */
    setRemoteMedia(uid, mediaType, track) {
        this.remoteMedia.set(`${uid}:${mediaType}`, track);
    }
    getRemoteStream(uid, mediaType) {
        const track = this.remoteMedia.get(`${uid}:${mediaType}`);
        if (!track)
            return null;
        return { getTracks: () => [track] };
    }
    async startScreenShare() {
        this.requireConnected();
        if (this.role === 'audience' || this.publishDenied) {
            throw rtcError('RTC_PUBLISH_DENIED', 'screen sharing is denied for this participant');
        }
        this.screenShared = true;
    }
    async stopScreenShare() {
        this.screenShared = false;
    }
    async subscribe(uid, mediaType) {
        this.requireConnected();
        const current = this.subscriptions.get(uid) ?? new Set();
        if (mediaType === 'both') {
            current.add('audio');
            current.add('video');
        }
        else {
            current.add(mediaType);
        }
        this.subscriptions.set(uid, current);
    }
    async unsubscribe(uid, mediaType) {
        this.requireConnected();
        const current = this.subscriptions.get(uid);
        if (!current)
            return;
        if (mediaType === 'both')
            current.clear();
        else
            current.delete(mediaType);
        this.subscriptions.set(uid, current);
    }
    async setRemoteVideoStreamType(uid, streamType) {
        this.streamTypes.set(uid, streamType);
    }
    async setSubscribeVisibleOnly(enabled) {
        this.visibleOnly = enabled;
    }
    async setVideoEncoderConfiguration(config) {
        this.encoder = { ...config };
    }
    async enableDualStreamMode(enabled) {
        this.dualStream = enabled;
    }
    async setDynacast(enabled) {
        this.dynacast = enabled;
    }
    async sendData(message) {
        this.requireConnected();
        const payload = toBytes(message.payload);
        if (payload.byteLength === 0)
            throw rtcError('invalid_parameter', 'data payload must not be empty');
        const to = message.to === undefined ? [] : Array.isArray(message.to) ? message.to : [message.to];
        this.dataMessages.push({
            to,
            payload,
            reliable: message.reliable ?? true,
            topic: message.topic ?? null,
        });
    }
    async getStats(uid) {
        const target = uid ?? this.localUid;
        return { ...(this.statsByUid.get(target) ?? emptyStats()) };
    }
    async startPreview() {
        this.previewing = true;
    }
    async stopPreview() {
        this.previewing = false;
    }
    async playAudioEffect(effect) {
        this.emitter.emit('audio-effect-finished', { effect });
    }
    async renewToken(token) {
        this.previewToken = token;
    }
    async dispose() {
        await this.disconnect();
        this.emitter.removeAll();
        this.participants.clear();
        this.subscriptions.clear();
        this.remoteMedia.clear();
    }
    // ---------------------------------------------------------------- simulation hooks
    setConnectFailure(failure) {
        this.connectFailure = failure;
    }
    failNextConnect(failure) {
        this.onceFailure = failure;
    }
    setPublishDenied(denied) {
        this.publishDenied = denied;
    }
    setStats(stats, uid) {
        this.statsByUid.set(uid ?? this.localUid, toRtcStats(stats));
    }
    /** Simulate a dropped media link without leaving the channel (drives the reconnect path). */
    simulateNetworkDrop(reason = 'network-drop') {
        this.state = 'disconnected';
        this.emitter.emit('connection-state', { state: 'disconnected', reason });
    }
    /** Make the next N connect() calls fail, so a reconnect scenario can exhaust retries. */
    failNextConnects(count) {
        this.reconnectFailureBudget = Math.max(0, Math.trunc(count));
    }
    simulateUserJoined(participant) {
        const state = {
            ...participant,
            published: new Set(),
            muted: new Set(),
        };
        this.participants.set(participant.uid, state);
        this.emitter.emit('participant-joined', toParticipantSnapshot(state));
    }
    simulateUserLeft(uid, reason = 'leave') {
        const participant = this.participants.get(uid);
        if (!participant)
            return;
        this.participants.delete(uid);
        this.emitter.emit('participant-left', {
            participant: toParticipantSnapshot(participant),
            reason,
        });
    }
    simulateTrackPublished(uid, mediaType, source) {
        const participant = this.participants.get(uid);
        participant?.published.add(mediaType);
        this.emitter.emit('track-published', {
            uid,
            mediaType,
            source: source ?? (mediaType === 'audio' ? 'microphone' : 'camera'),
            userId: participant?.userId ?? null,
        });
    }
    simulateTrackUnpublished(uid, mediaType, source) {
        const participant = this.participants.get(uid);
        participant?.published.delete(mediaType);
        this.emitter.emit('track-unpublished', {
            uid,
            mediaType,
            source: source ?? (mediaType === 'audio' ? 'microphone' : 'camera'),
            userId: participant?.userId ?? null,
        });
    }
    simulateTrackMuted(uid, mediaType, muted) {
        const participant = this.participants.get(uid);
        if (muted)
            participant?.muted.add(mediaType);
        else
            participant?.muted.delete(mediaType);
        this.emitter.emit('track-muted', {
            uid,
            mediaType,
            muted,
            userId: participant?.userId ?? null,
        });
    }
    simulateRoleChanged(uid, role) {
        const participant = this.participants.get(uid);
        if (participant)
            participant.role = role;
        this.emitter.emit('role-changed', { uid, role });
    }
    simulateData(from, payload, topic) {
        const participant = this.participants.get(from);
        this.emitter.emit('data-received', {
            from,
            payload: toBytes(payload),
            topic: topic ?? null,
            userId: participant?.userId ?? null,
        });
    }
    simulateVolume(uid, level) {
        this.emitter.emit('volume', { uid, level });
    }
    simulateConnectionState(state, reason = null) {
        this.state = state;
        if (state !== 'connected')
            this.joined = false;
        this.emitter.emit('connection-state', { state, reason });
    }
    // ---------------------------------------------------------------- assertions
    getConnectAttempts() {
        return this.connectAttempts;
    }
    getDisconnectCount() {
        return this.disconnectCount;
    }
    getLocalUid() {
        return this.localUid;
    }
    getRole() {
        return this.role;
    }
    getPublished() {
        return { audio: this.audioPublished, video: this.videoPublished, screen: this.screenShared };
    }
    getSubscriptions() {
        const snapshot = new Map();
        for (const [uid, set] of this.subscriptions)
            snapshot.set(uid, [...set]);
        return snapshot;
    }
    getRemoteVideoStreamType(uid) {
        return this.streamTypes.get(uid);
    }
    getEncoderConfiguration() {
        return this.encoder ? { ...this.encoder } : null;
    }
    isDualStreamEnabled() {
        return this.dualStream;
    }
    isDynacastEnabled() {
        return this.dynacast;
    }
    isSubscribeVisibleOnly() {
        return this.visibleOnly;
    }
    isPreviewing() {
        return this.previewing;
    }
    getDataMessages() {
        return this.dataMessages.map((message) => ({
            ...message,
            payload: new Uint8Array(message.payload),
        }));
    }
    getCurrentToken() {
        return this.previewToken;
    }
    getClock() {
        return this.clock;
    }
    requireConnected() {
        if (!this.isConnected()) {
            throw rtcError('channel_not_found', 'not in a channel; join first', { sdkCode: -113 });
        }
    }
}
function toParticipantSnapshot(state) {
    return { uid: state.uid, userId: state.userId, role: state.role };
}
function failureText(failure) {
    if (failure instanceof Error)
        return failure.message;
    return String(failure);
}
function toBytes(payload) {
    if (typeof payload === 'string')
        return new TextEncoder().encode(payload);
    return new Uint8Array(payload);
}
/** Create a deterministic fake transport (test utility, also useful for offline demos). */
export function createFakeTransport(options = {}) {
    return new FakeTransport(options);
}
const MEDIA_SOURCE_KEYS = {
    camera: 'Camera',
    microphone: 'Microphone',
    screen: 'ScreenShare',
    'screen-audio': 'ScreenShareAudio',
};
function moduleSource(mod, source) {
    return mod.Track.Source[MEDIA_SOURCE_KEYS[source]] ?? MEDIA_SOURCE_KEYS[source] ?? source;
}
function sourceFromModuleValue(mod, value) {
    for (const [source, key] of Object.entries(MEDIA_SOURCE_KEYS)) {
        if (mod.Track.Source[key] === value)
            return source;
    }
    return value === 'screen_share_audio' ? 'screen-audio' : 'camera';
}
function mediaTypeOf(kind) {
    return kind === 'audio' ? 'audio' : 'video';
}
function uidFromIdentity(identity) {
    const parsed = Number.parseInt(identity, 10);
    if (Number.isInteger(parsed) && parsed > 0 && parsed <= 2147483647)
        return parsed;
    // Deterministic fallback for non-numeric identities (external identity providers).
    let hash = 2166136261;
    for (let index = 0; index < identity.length; index += 1) {
        hash ^= identity.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0) % 2147483647 || 1;
}
function participantFromMetadata(identity, name, metadata) {
    const parsed = {};
    if (metadata) {
        try {
            const candidate = JSON.parse(metadata);
            if (typeof candidate.userId === 'string')
                parsed.userId = candidate.userId;
            if (candidate.role === 'host' || candidate.role === 'audience')
                parsed.role = candidate.role;
        }
        catch {
            /* metadata is host-defined; ignore malformed JSON */
        }
    }
    return {
        uid: uidFromIdentity(identity),
        userId: parsed.userId ?? name ?? null,
        role: parsed.role ?? 'host',
    };
}
function pickNumber(record, key) {
    const value = record[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}
/**
 * SFU media transport (browser implementation). The provider module is imported lazily on first
 * connect so importing the SDK never loads the media stack, and so the facade builds and
 * unit-tests without it at runtime.
 */
export class SfuTransport {
    kind = 'sfu';
    emitter;
    logger;
    clock;
    bitrateCache = new Map();
    streamTypes = new Map();
    subscriptions = new Map();
    // Stable MediaStream wrappers: React renders call getLocalStream/getRemoteStream repeatedly, and
    // returning a fresh MediaStream each time makes <video>.srcObject reset on every render, which
    // prevents frames from ever being decoded. Cache one stream per source and swap tracks inside it.
    localStreams = new Map();
    remoteStreams = new Map();
    moduleLoader;
    media = null;
    modulePromise = null;
    room = null;
    loadedModule = null;
    options = null;
    localUid = 0;
    previewStream = null;
    mutedState = { audio: false, video: false };
    encoder = null;
    dualStream = false;
    constructor(options = {}) {
        this.logger = options.logger;
        this.clock = options.clock;
        this.moduleLoader = options.moduleLoader ?? null;
        this.emitter = new Emitter((error, event) => {
            this.logger?.warn('rtc-web: transport event listener failed', String(event), error);
        });
    }
    on(event, listener) {
        return this.emitter.on(event, listener);
    }
    async connect(options) {
        const mod = await this.loadModule();
        this.loadedModule = mod;
        await this.disconnectRoomOnly();
        this.options = options;
        this.localUid = options.uid;
        this.encoder = options.encoder ?? null;
        this.dualStream = options.dualStream;
        const room = new mod.Room({
            adaptiveStream: true,
            dynacast: options.dynacast,
            stopLocalTrackOnUnpublish: true,
            videoCaptureDefaults: {
                ...(options.videoDeviceId ? { deviceId: options.videoDeviceId } : {}),
                ...(options.encoder
                    ? {
                        resolution: {
                            width: options.encoder.width,
                            height: options.encoder.height,
                            frameRate: options.encoder.frameRate,
                        },
                    }
                    : {}),
            },
            ...(options.audioDeviceId
                ? { audioCaptureDefaults: { deviceId: options.audioDeviceId } }
                : {}),
            publishDefaults: buildPublishDefaults(mod, options.encoder, options.dualStream),
        });
        this.room = room;
        this.wireRoom(room, mod);
        this.emitter.emit('connection-state', { state: 'connecting', reason: null });
        try {
            await room.connect(options.url, options.token, {
                autoSubscribe: options.autoSubscribe,
                ...(options.iceServers?.length ? { rtcConfig: { iceServers: options.iceServers } } : {}),
            });
            if (options.publishAudio)
                await room.localParticipant.setMicrophoneEnabled(true);
            if (options.publishVideo)
                await room.localParticipant.setCameraEnabled(true);
        }
        catch (error) {
            await this.disconnectRoomOnly();
            throw normalizeError(error, 'upstream_unavailable');
        }
        this.emitter.emit('connection-state', { state: 'connected', reason: null });
        return {
            uid: options.uid,
            participants: [...room.remoteParticipants.values()].map((participant) => participantFromMetadata(participant.identity, participant.name, participant.metadata)),
        };
    }
    async disconnect() {
        await this.disconnectRoomOnly();
        this.emitter.emit('connection-state', { state: 'disconnected', reason: 'local-leave' });
    }
    isConnected() {
        return this.room !== null && this.room.state === 'connected';
    }
    async publish(mediaType) {
        const room = this.requireRoom();
        try {
            if (mediaType === 'audio')
                await room.localParticipant.setMicrophoneEnabled(true);
            else
                await room.localParticipant.setCameraEnabled(true);
        }
        catch (error) {
            throw normalizeError(error);
        }
    }
    async unpublish(mediaType) {
        const room = this.requireRoom();
        const mod = await this.loadModule();
        const source = mediaType === 'audio' ? moduleSource(mod, 'microphone') : moduleSource(mod, 'camera');
        const publication = room.localParticipant.getTrackPublication(source);
        if (publication?.track) {
            try {
                await room.localParticipant.unpublishTrack(publication.track, true);
            }
            catch (error) {
                throw normalizeError(error);
            }
        }
    }
    async setMuted(mediaType, muted) {
        const room = this.requireRoom();
        const mod = await this.loadModule();
        const source = mediaType === 'audio' ? moduleSource(mod, 'microphone') : moduleSource(mod, 'camera');
        const publication = room.localParticipant.getTrackPublication(source);
        const track = publication?.track;
        try {
            // Turning a camera back on after it was muted can leave the underlying MediaStreamTrack
            // ended (the browser releases the device). In that case re-enable the camera through the
            // participant API so LiveKit captures and publishes a fresh track instead of unmuting a dead
            // one; otherwise keep the cheaper mute/unmute path.
            const cameraEnded = mediaType === 'video' &&
                !muted &&
                track?.mediaStreamTrack?.readyState === 'ended';
            if (cameraEnded && typeof room.localParticipant.setCameraEnabled === 'function') {
                await room.localParticipant.setCameraEnabled(true);
            }
            else if (track?.mute && track.unmute) {
                await (muted ? track.mute() : track.unmute());
            }
            else if (track?.mediaStreamTrack) {
                track.mediaStreamTrack.enabled = !muted;
            }
            else {
                throw rtcError('invalid_parameter', `${mediaType} is not published; call publish first`);
            }
        }
        catch (error) {
            throw normalizeError(error, 'invalid_parameter');
        }
        this.mutedState[mediaType] = muted;
    }
    async publishTrack(track, source) {
        const room = this.requireRoom();
        const mod = await this.loadModule();
        if (typeof room.localParticipant.publishTrack !== 'function') {
            throw rtcError('invalid_parameter', 'the media provider does not support publishing provided tracks');
        }
        try {
            await room.localParticipant.publishTrack(track, { source: moduleSource(mod, source) });
        }
        catch (error) {
            throw normalizeError(error);
        }
    }
    async unpublishTrack(source) {
        const room = this.room;
        if (!room)
            return;
        const mod = await this.loadModule();
        const publication = room.localParticipant.getTrackPublication(moduleSource(mod, source));
        if (publication?.track) {
            try {
                await room.localParticipant.unpublishTrack(publication.track, true);
            }
            catch (error) {
                throw normalizeError(error);
            }
        }
    }
    isMuted(mediaType) {
        return this.mutedState[mediaType];
    }
    getLocalStream(source = 'camera') {
        const mod = this.loadedModule;
        const room = this.room;
        if (!mod || !room)
            return null;
        const publication = room.localParticipant.getTrackPublication(moduleSource(mod, source));
        const track = publication?.track?.mediaStreamTrack;
        if (!track) {
            this.localStreams.delete(source);
            return null;
        }
        return this.stableStream(this.localStreams, source, track);
    }
    getRemoteStream(uid, mediaType) {
        const room = this.room;
        if (!room)
            return null;
        const participant = room.remoteParticipants.get(String(uid));
        if (!participant)
            return null;
        const key = `${uid}:${mediaType}`;
        for (const publication of participant.trackPublications.values()) {
            if (publication.kind !== mediaType)
                continue;
            const track = publication.track?.mediaStreamTrack;
            if (track)
                return this.stableStream(this.remoteStreams, key, track);
        }
        this.remoteStreams.delete(key);
        return null;
    }
    /** Reuse the cached MediaStream and only replace its track when the underlying track changes. */
    stableStream(cache, key, track) {
        let stream = cache.get(key);
        if (!stream) {
            stream = new MediaStream();
            cache.set(key, stream);
        }
        const current = stream.getTracks();
        if (current.length !== 1 || current[0]?.id !== track.id) {
            for (const previous of current)
                stream.removeTrack(previous);
            stream.addTrack(track);
        }
        return stream;
    }
    async startScreenShare() {
        const room = this.requireRoom();
        try {
            await room.localParticipant.setScreenShareEnabled(true);
        }
        catch (error) {
            throw normalizeError(error, 'RTC_PUBLISH_DENIED');
        }
    }
    async stopScreenShare() {
        const room = this.room;
        if (!room)
            return;
        const mod = await this.loadModule();
        const publication = room.localParticipant.getTrackPublication(moduleSource(mod, 'screen'));
        if (publication?.track) {
            try {
                await room.localParticipant.unpublishTrack(publication.track, true);
            }
            catch (error) {
                throw normalizeError(error);
            }
        }
    }
    async subscribe(uid, mediaType) {
        this.subscriptions.set(uid, mergeSubscription(this.subscriptions.get(uid), mediaType));
        await this.applySubscription(uid, mediaType, true);
    }
    async unsubscribe(uid, mediaType) {
        const remaining = mergeSubscription(this.subscriptions.get(uid), mediaType);
        this.subscriptions.set(uid, remaining.filter((entry) => !(mediaType === 'both' || entry === mediaType)));
        await this.applySubscription(uid, mediaType, false);
    }
    async setRemoteVideoStreamType(uid, streamType) {
        this.streamTypes.set(uid, streamType);
        const participant = this.findRemote(uid);
        if (!participant)
            return;
        const mod = await this.loadModule();
        const publication = participant.getTrackPublication(moduleSource(mod, 'camera'));
        try {
            // LiveKit encodes LOW=0, MEDIUM=1, HIGH=2.
            publication?.setVideoQuality?.(streamType === 'high' ? 2 : 0);
        }
        catch (error) {
            throw normalizeError(error);
        }
    }
    async setSubscribeVisibleOnly(enabled) {
        if (this.room?.options)
            this.room.options.adaptiveStream = enabled;
    }
    async setVideoEncoderConfiguration(config) {
        this.encoder = { ...config };
        // Simulcast layers are negotiated at publish time; applied on the next publish/join.
        this.logger?.debug('rtc-web: encoder configuration stored; applied on the next publish', config);
    }
    async enableDualStreamMode(enabled) {
        this.dualStream = enabled;
    }
    async setDynacast(enabled) {
        if (this.room?.options)
            this.room.options.dynacast = enabled;
    }
    async sendData(message) {
        const room = this.requireRoom();
        const payload = typeof message.payload === 'string'
            ? new TextEncoder().encode(message.payload)
            : message.payload;
        const destination = message.to === undefined ? [] : Array.isArray(message.to) ? message.to : [message.to];
        try {
            await room.localParticipant.publishData(payload, {
                reliable: message.reliable ?? true,
                ...(message.topic !== undefined ? { topic: message.topic } : {}),
                ...(destination.length > 0 ? { destinationIdentities: destination.map(String) } : {}),
            });
        }
        catch (error) {
            throw normalizeError(error, 'upstream_unavailable');
        }
    }
    async getStats(uid) {
        const room = this.room;
        if (!room)
            return emptyStats();
        try {
            const target = uid ?? this.localUid;
            if (target === this.localUid)
                return await this.collectLocalStats(room);
            const participant = this.findRemote(target);
            if (!participant)
                return emptyStats();
            return await this.collectRemoteStats(target, participant);
        }
        catch (error) {
            this.logger?.debug('rtc-web: stats collection failed', error);
            return emptyStats();
        }
    }
    async startPreview() {
        const mediaDevices = globalThis.navigator?.mediaDevices;
        if (!mediaDevices?.getUserMedia) {
            throw rtcError('RTC_DEVICE_NOT_FOUND', 'media devices are not available in this environment');
        }
        if (this.previewStream)
            return;
        try {
            this.previewStream = await mediaDevices.getUserMedia({ audio: true, video: true });
        }
        catch (error) {
            throw normalizeError(error);
        }
    }
    async stopPreview() {
        if (!this.previewStream)
            return;
        for (const track of this.previewStream.getTracks())
            track.stop();
        this.previewStream = null;
    }
    async playAudioEffect(effect) {
        // Playback is a UI concern; the event is emitted so hosts can drive their own audio graph.
        this.emitter.emit('audio-effect-finished', { effect });
    }
    async renewToken(token) {
        // LiveKit refreshes the signal credential itself; the control-plane token stays authoritative.
        if (this.options)
            this.options = { ...this.options, token };
    }
    async dispose() {
        await this.stopPreview();
        await this.disconnectRoomOnly();
        this.emitter.removeAll();
    }
    requireRoom() {
        if (!this.room)
            throw rtcError('channel_not_found', 'not in a channel; join first', { sdkCode: -113 });
        return this.room;
    }
    async loadModule() {
        if (this.media)
            return this.media;
        if (!this.modulePromise) {
            this.modulePromise = (async () => {
                try {
                    const imported = this.moduleLoader
                        ? await this.moduleLoader()
                        : await (await import('@nimeet/adapter-browser')).loadLiveKitClient();
                    const candidate = imported;
                    if (typeof candidate.Room !== 'function')
                        throw new Error('the module exports no Room class');
                    return candidate;
                }
                catch (error) {
                    throw rtcError('internal_error', "the browser media transport is unavailable: install '@nimeet/adapter-browser' or pass a custom transport", { cause: error });
                }
            })();
        }
        this.media = await this.modulePromise;
        return this.media;
    }
    async disconnectRoomOnly() {
        const room = this.room;
        this.room = null;
        this.localStreams.clear();
        this.remoteStreams.clear();
        if (!room)
            return;
        try {
            await room.disconnect(true);
        }
        catch (error) {
            this.logger?.debug('rtc-web: room disconnect failed', error);
        }
    }
    findRemote(uid) {
        if (!this.room)
            return null;
        return this.room.remoteParticipants.get(String(uid)) ?? null;
    }
    wireRoom(room, mod) {
        const events = mod.RoomEvent;
        room.on(events.ConnectionStateChanged ?? 'connectionStateChanged', (...args) => {
            const state = String(args[0] ?? '');
            this.emitter.emit('connection-state', { state: mapConnectionState(state), reason: null });
        });
        room.on(events.Reconnecting ?? 'reconnecting', () => {
            this.emitter.emit('connection-state', {
                state: 'reconnecting',
                reason: 'signal-reconnecting',
            });
        });
        room.on(events.Reconnected ?? 'reconnected', () => {
            this.emitter.emit('connection-state', { state: 'connected', reason: 'reconnected' });
        });
        room.on(events.Disconnected ?? 'disconnected', () => {
            this.emitter.emit('connection-state', {
                state: 'disconnected',
                reason: 'transport-disconnected',
            });
        });
        room.on(events.ParticipantConnected ?? 'participantConnected', (...args) => {
            const participant = args[0];
            this.emitter.emit('participant-joined', participantFromMetadata(participant.identity, participant.name, participant.metadata));
        });
        room.on(events.ParticipantDisconnected ?? 'participantDisconnected', (...args) => {
            const participant = args[0];
            this.emitter.emit('participant-left', {
                participant: participantFromMetadata(participant.identity, participant.name, participant.metadata),
                reason: 'disconnected',
            });
        });
        const onSubscribed = (...args) => {
            const publication = args[1];
            const participant = args[2];
            this.emitter.emit('track-published', {
                uid: uidFromIdentity(participant.identity),
                mediaType: mediaTypeOf(publication.kind),
                source: sourceFromModuleValue(mod, publication.source),
                userId: participant.name ?? null,
            });
        };
        const onUnsubscribed = (...args) => {
            const publication = args[1];
            const participant = args[2];
            this.emitter.emit('track-unpublished', {
                uid: uidFromIdentity(participant.identity),
                mediaType: mediaTypeOf(publication.kind),
                source: sourceFromModuleValue(mod, publication.source),
                userId: participant.name ?? null,
            });
        };
        room.on(events.TrackSubscribed ?? 'trackSubscribed', onSubscribed);
        room.on(events.TrackUnsubscribed ?? 'trackUnsubscribed', onUnsubscribed);
        // LiveKit >= 1.13 emits TrackUnpublished when a remote participant removes a publication;
        // TrackUnsubscribed is not guaranteed in that path. Forward both, idempotently.
        const onUnpublished = (...args) => {
            const publication = args[0];
            const participant = args[1];
            this.emitter.emit('track-unpublished', {
                uid: uidFromIdentity(participant.identity),
                mediaType: mediaTypeOf(publication.kind),
                source: sourceFromModuleValue(mod, publication.source),
                userId: participant.name ?? null,
            });
        };
        room.on(events.TrackUnpublished ?? 'trackUnpublished', onUnpublished);
        const onMuted = (muted) => {
            return (...args) => {
                const publication = args[0];
                const participant = args[1];
                this.emitter.emit('track-muted', {
                    uid: uidFromIdentity(participant.identity),
                    mediaType: mediaTypeOf(publication.kind),
                    muted,
                    userId: participant.name ?? null,
                });
            };
        };
        room.on(events.TrackMuted ?? 'trackMuted', onMuted(true));
        room.on(events.TrackUnmuted ?? 'trackUnmuted', onMuted(false));
        room.on(events.ActiveSpeakersChanged ?? 'activeSpeakersChanged', (...args) => {
            const speakers = args[0];
            if (!Array.isArray(speakers))
                return;
            for (const speaker of speakers) {
                this.emitter.emit('volume', {
                    uid: uidFromIdentity(speaker.identity),
                    level: Math.round(Math.min(1, Math.max(0, speaker.audioLevel ?? 0)) * 255),
                });
            }
        });
        room.on(events.DataReceived ?? 'dataReceived', (...args) => {
            const payload = args[0];
            const participant = args[1];
            const topic = typeof args[3] === 'string' ? args[3] : null;
            if (!(payload instanceof Uint8Array) || !participant)
                return;
            this.emitter.emit('data-received', {
                from: uidFromIdentity(participant.identity),
                payload,
                topic,
                userId: participant.name ?? null,
            });
        });
    }
    async applySubscription(uid, mediaType, subscribed) {
        const participant = this.findRemote(uid);
        if (!participant)
            return;
        const mod = await this.loadModule();
        const sources = mediaType === 'both'
            ? [moduleSource(mod, 'camera'), moduleSource(mod, 'microphone')]
            : [moduleSource(mod, mediaType === 'audio' ? 'microphone' : 'camera')];
        try {
            for (const source of sources) {
                const publication = participant.getTrackPublication(source);
                publication?.setSubscribed?.(subscribed);
            }
        }
        catch (error) {
            throw normalizeError(error);
        }
    }
    async collectLocalStats(room) {
        const mod = await this.loadModule();
        const videoPublication = room.localParticipant.getTrackPublication(moduleSource(mod, 'camera'));
        let video = null;
        const senderStats = await videoPublication?.track?.getSenderStats?.();
        if (Array.isArray(senderStats) && senderStats.length > 0) {
            const sorted = senderStats;
            video = sorted[0] ?? null;
            for (const entry of sorted) {
                if (pickNumber(entry, 'frameWidth') !== undefined)
                    video = entry;
            }
        }
        const bitrate = this.computeBitrate('local:video', pickNumber(video ?? {}, 'bytesSent'), pickNumber(video ?? {}, 'timestamp'));
        return toRtcStats({
            rttMs: pickNumber(video ?? {}, 'roundTripTime'),
            packetLoss: ratio(pickNumber(video ?? {}, 'packetsLost'), pickNumber(video ?? {}, 'packetsSent')),
            jitterMs: pickNumber(video ?? {}, 'jitter'),
            bitrateKbps: bitrate,
            width: pickNumber(video ?? {}, 'frameWidth'),
            height: pickNumber(video ?? {}, 'frameHeight'),
            framesPerSecond: pickNumber(video ?? {}, 'framesPerSecond'),
            freezeCount: 0,
        });
    }
    async collectRemoteStats(uid, participant) {
        const mod = await this.loadModule();
        const videoPublication = participant.getTrackPublication(moduleSource(mod, 'camera'));
        const receiver = await videoPublication?.track?.getReceiverStats?.();
        const record = isRecord(receiver) ? receiver : {};
        const packetsLost = pickNumber(record, 'packetsLost');
        const packetsReceived = pickNumber(record, 'packetsReceived');
        const bitrate = this.computeBitrate(`remote:${uid}:video`, pickNumber(record, 'bytesReceived'), pickNumber(record, 'timestamp'));
        return toRtcStats({
            packetLoss: ratio(packetsLost, packetsReceived),
            jitterMs: pickNumber(record, 'jitter'),
            bitrateKbps: bitrate,
            width: pickNumber(record, 'frameWidth'),
            height: pickNumber(record, 'frameHeight'),
            framesPerSecond: pickNumber(record, 'framesPerSecond'),
            freezeCount: 0,
        });
    }
    computeBitrate(key, bytes, timestamp) {
        if (bytes === undefined || timestamp === undefined)
            return 0;
        const previous = this.bitrateCache.get(key);
        this.bitrateCache.set(key, { bytes, timestamp });
        if (!previous || timestamp <= previous.timestamp)
            return 0;
        const elapsedSeconds = (timestamp - previous.timestamp) / 1000;
        if (elapsedSeconds <= 0)
            return 0;
        const bits = Math.max(0, bytes - previous.bytes) * 8;
        const kbps = bits / 1000 / elapsedSeconds;
        return Number.isFinite(kbps) ? Math.round(kbps) : 0;
    }
}
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
function ratio(numerator, denominator) {
    if (numerator === undefined || denominator === undefined)
        return 0;
    const total = numerator + denominator;
    if (total <= 0)
        return 0;
    return Math.min(1, Math.max(0, numerator / total));
}
function mergeSubscription(current, mediaType) {
    const next = new Set(current ?? []);
    if (mediaType === 'both') {
        next.add('audio');
        next.add('video');
    }
    else {
        next.add(mediaType);
    }
    return [...next];
}
function mapConnectionState(state) {
    switch (state) {
        case 'connected':
            return 'connected';
        case 'connecting':
            return 'connecting';
        case 'reconnecting':
        case 'signalReconnecting':
            return 'reconnecting';
        case 'disconnected':
            return 'disconnected';
        default:
            return 'disconnected';
    }
}
function buildPublishDefaults(mod, encoder, dualStream) {
    const degradation = mapDegradationPreference(encoder?.degradationPreference);
    if (!encoder) {
        return {
            simulcast: dualStream,
            ...(degradation ? { degradationPreference: degradation } : {}),
        };
    }
    const layers = [];
    if (dualStream) {
        const lowWidth = Math.max(2, Math.round(encoder.width / 2));
        const lowHeight = Math.max(2, Math.round(encoder.height / 2));
        const bitrateMax = encoder.bitrateMax ?? 1500;
        layers.push(new mod.VideoPreset(lowWidth, lowHeight, Math.max(150, Math.round(bitrateMax / 2)), Math.max(1, Math.round(encoder.frameRate / 2))));
        layers.push(new mod.VideoPreset(encoder.width, encoder.height, bitrateMax, encoder.frameRate));
    }
    return {
        simulcast: dualStream,
        ...(degradation ? { degradationPreference: degradation } : {}),
        videoEncoding: {
            maxBitrate: encoder.bitrateMax ?? 1500,
            maxFramerate: encoder.frameRate,
        },
        ...(layers.length > 0 ? { videoSimulcastLayers: layers } : {}),
    };
}
function mapDegradationPreference(preference) {
    switch (preference) {
        case 'maintain-framerate':
            return 'maintain-framerate';
        case 'maintain-resolution':
            return 'maintain-resolution';
        case 'balanced':
            return 'balanced';
        default:
            return undefined;
    }
}
//# sourceMappingURL=transport.js.map